import React, { useState } from "react";
import {
    Search,
    Star,
    Calendar,
    Clock,
    Video,
    MessageSquare,
    CheckCircle2,
    XCircle,
    IndianRupee,
    TrendingUp,
    Users,
    Award,
    Bell,
    MapPin,
    ChevronRight,
} from "lucide-react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
} from "recharts";

// ---- Sample data (swap for real API data) ----------------------------------
const expert = {
    name: "Rahul",
    title: "CAD Design Expert",
    skills: ["SolidWorks", "AutoCAD", "Fusion 360", "GD&T"],
    location: "Bengaluru, KA",
    rating: 4.9,
    reviews: 86,
    bio: "12+ years helping manufacturers move from concept to production-ready CAD models.",
};

const bookingTrend = [
    { day: "Mon", bookings: 3 },
    { day: "Tue", bookings: 5 },
    { day: "Wed", bookings: 2 },
    { day: "Thu", bookings: 6 },
    { day: "Fri", bookings: 4 },
    { day: "Sat", bookings: 7 },
    { day: "Sun", bookings: 3 },
];

const sessionTypes = [
    { name: "Product Demo", value: 42, color: "#6366F1" },
    { name: "1:1 Consultation", value: 28, color: "#0EA5E9" },
    { name: "Team Training", value: 18, color: "#F59E0B" },
    { name: "Design Review", value: 12, color: "#22C55E" },
];

const monthlyEarnings = [
    { month: "Feb", amount: 32000 },
    { month: "Mar", amount: 41000 },
    { month: "Apr", amount: 38500 },
    { month: "May", amount: 52000 },
    { month: "Jun", amount: 47500 },
    { month: "Jul", amount: 61000 },
];

const upcomingBookings = [
    {
        id: "BK-2231",
        user: "Apex Robotics Pvt Ltd",
        topic: "Fusion 360 assembly walkthrough for sensor housing",
        type: "Product Demo",
        date: "2026-07-22",
        time: "11:00 AM",
        duration: "45 min",
        status: "confirmed",
    },
    {
        id: "BK-2230",
        user: "Sharma Auto Components",
        topic: "GD&T review for bracket tolerances",
        type: "Design Review",
        date: "2026-07-22",
        time: "3:30 PM",
        duration: "30 min",
        status: "confirmed",
    },
    {
        id: "BK-2229",
        user: "Metro Industrial Buyers",
        topic: "Intro to SolidWorks sheet metal tools",
        type: "1:1 Consultation",
        date: "2026-07-23",
        time: "10:00 AM",
        duration: "60 min",
        status: "pending",
    },
    {
        id: "BK-2225",
        user: "Anand Mobility Ltd",
        topic: "Team onboarding — AutoCAD basics",
        type: "Team Training",
        date: "2026-07-24",
        time: "2:00 PM",
        duration: "90 min",
        status: "pending",
    },
];

const reviews = [
    {
        user: "Apex Robotics",
        rating: 5,
        comment: "Explained the assembly constraints clearly, saved us hours of rework.",
    },
    {
        user: "PrintForm Industries",
        rating: 5,
        comment: "Very patient walking our junior designer through GD&T basics.",
    },
    {
        user: "Metro Metal Works",
        rating: 4,
        comment: "Good session, ran a little over time but content was solid.",
    },
];

// ---- Custom scrollbar --------------------------------------------------------
function ScrollbarStyles() {
    return (
        <style>{`
      .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
      .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: #CBD5E1;
        border-radius: 9999px;
        border: 2px solid transparent;
        background-clip: padding-box;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94A3B8; }
      .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #CBD5E1 transparent; }
    `}</style>
    );
}

const currency = (n) => "\u20B9" + n.toLocaleString("en-IN");
const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

const STATUS_STYLE = {
    confirmed: { label: "Confirmed", bg: "#EAFBF1", text: "#15803D", dot: "#22C55E" },
    pending: { label: "Awaiting your response", bg: "#FFF7E6", text: "#B45309", dot: "#F59E0B" },
};

// ---- Stat card ----------------------------------------------------------------
function StatCard({ label, value, sub, icon: Icon, accent }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                    {label}
                </span>
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: accent + "15" }}
                >
                    <Icon size={16} style={{ color: accent }} />
                </div>
            </div>
            <span className="text-2xl font-bold text-slate-900">{value}</span>
            {sub && <span className="text-xs text-slate-400">{sub}</span>}
        </div>
    );
}

// ---- Custom tooltip for charts ------------------------------------------------
function ChartTooltip({ active, payload, label, formatter }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs">
            <div className="font-semibold text-slate-700 mb-0.5">{label}</div>
            {payload.map((p, i) => (
                <div key={i} style={{ color: p.color || p.fill }}>
                    {formatter ? formatter(p.value) : p.value}
                </div>
            ))}
        </div>
    );
}

// ---- Main page -----------------------------------------------------------------
export default function ExpertsDashboard() {
    const [bookings, setBookings] = useState(upcomingBookings);
    const [query, setQuery] = useState("");

    const stats = {
        upcoming: bookings.filter((b) => b.status !== "declined").length,
        pending: bookings.filter((b) => b.status === "pending").length,
        completedThisMonth: 34,
        earningsThisMonth: monthlyEarnings[monthlyEarnings.length - 1].amount,
    };

    const filtered = bookings.filter((b) => {
        const q = query.toLowerCase();
        return (
            b.user.toLowerCase().includes(q) ||
            b.topic.toLowerCase().includes(q) ||
            b.type.toLowerCase().includes(q)
        );
    });

    const respond = (id, status) => {
        setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    };

    return (
        <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-8 px-6 font-sans">
            <ScrollbarStyles />
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header / profile */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg flex-shrink-0">
                            {expert.name.split(" ").map((w) => w[0]).join("")}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-xl font-bold text-slate-900">{expert.name}</h1>
                                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full">
                                    {expert.title}
                                </span>
                            </div>
                            <p className="text-sm text-slate-500 mt-1 max-w-lg">{expert.bio}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-2 flex-wrap">
                                <span className="flex items-center gap-1">
                                    <MapPin size={12} /> {expert.location}
                                </span>
                                <span className="flex items-center gap-1">
                                    <Star size={12} className="fill-amber-400 text-amber-400" />
                                    {expert.rating} ({expert.reviews} reviews)
                                </span>
                                <span className="flex items-center gap-1 flex-wrap">
                                    {expert.skills.map((s) => (
                                        <span
                                            key={s}
                                            className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button className="relative inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-600 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-slate-50">
                        <Bell size={15} /> Notifications
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                            {stats.pending}
                        </span>
                    </button>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        label="Upcoming Bookings"
                        value={stats.upcoming}
                        sub={`${stats.pending} awaiting response`}
                        icon={Calendar}
                        accent="#6366F1"
                    />
                    <StatCard
                        label="Sessions Completed"
                        value={stats.completedThisMonth}
                        sub="this month"
                        icon={CheckCircle2}
                        accent="#22C55E"
                    />
                    <StatCard
                        label="Earnings (MTD)"
                        value={currency(stats.earningsThisMonth)}
                        icon={IndianRupee}
                        accent="#0EA5E9"
                    />
                    <StatCard
                        label="Expert Rating"
                        value={expert.rating}
                        sub={`${expert.reviews} reviews`}
                        icon={Award}
                        accent="#EC4899"
                    />
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="font-bold text-slate-900 text-[15px]">Earnings Trend</h2>
                                <p className="text-xs text-slate-400 mt-0.5">Last 6 months</p>
                            </div>
                            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                                <TrendingUp size={13} /> +28% vs last month
                            </span>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={monthlyEarnings} margin={{ left: -10, right: 10 }}>
                                <defs>
                                    <linearGradient id="earningsFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                <XAxis
                                    dataKey="month"
                                    tick={{ fontSize: 12, fill: "#94A3B8" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 12, fill: "#94A3B8" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `₹${v / 1000}k`}
                                />
                                <Tooltip content={<ChartTooltip formatter={currency} />} />
                                <Area
                                    type="monotone"
                                    dataKey="amount"
                                    stroke="#6366F1"
                                    strokeWidth={2.5}
                                    fill="url(#earningsFill)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-6">
                        <h2 className="font-bold text-slate-900 text-[15px] mb-1">Session Mix</h2>
                        <p className="text-xs text-slate-400 mb-2">By type, this quarter</p>
                        <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                                <Pie
                                    data={sessionTypes}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={45}
                                    outerRadius={70}
                                    paddingAngle={3}
                                >
                                    {sessionTypes.map((s, i) => (
                                        <Cell key={i} fill={s.color} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip content={<ChartTooltip formatter={(v) => `${v}%`} />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-1.5 mt-2">
                            {sessionTypes.map((s) => (
                                <div key={s.name} className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-1.5 text-slate-600">
                                        <span
                                            className="w-2 h-2 rounded-full"
                                            style={{ backgroundColor: s.color }}
                                        />
                                        {s.name}
                                    </span>
                                    <span className="font-semibold text-slate-700">{s.value}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Weekly bookings bar chart */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="font-bold text-slate-900 text-[15px]">Bookings This Week</h2>
                            <p className="text-xs text-slate-400 mt-0.5">Demo & consultation requests by day</p>
                        </div>
                        <span className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Users size={13} /> 30 total this week
                        </span>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={bookingTrend} margin={{ left: -20, right: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis
                                dataKey="day"
                                tick={{ fontSize: 12, fill: "#94A3B8" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                            <Tooltip content={<ChartTooltip formatter={(v) => `${v} bookings`} />} />
                            <Bar dataKey="bookings" fill="#6366F1" radius={[6, 6, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Upcoming bookings */}
                    <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                            <h2 className="font-bold text-slate-900 text-[15px]">Demo & Session Requests</h2>
                            <div className="relative">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search bookings..."
                                    className="rounded-full border border-slate-200 bg-slate-50 pl-8 pr-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 w-44"
                                />
                            </div>
                        </div>

                        <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto custom-scrollbar">
                            {filtered.map((b) => {
                                const cfg = STATUS_STYLE[b.status];
                                return (
                                    <div key={b.id} className="px-6 py-4 hover:bg-slate-50/60 transition-colors">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-semibold text-slate-900 text-sm">{b.user}</span>
                                                    <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                                        {b.type}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-600 mt-1">{b.topic}</p>
                                                <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={12} /> {formatDate(b.date)}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={12} /> {b.time} &middot; {b.duration}
                                                    </span>
                                                </div>
                                            </div>
                                            <span
                                                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                                                style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.dot }} />
                                                {cfg.label}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 mt-3">
                                            {b.status === "pending" ? (
                                                <>
                                                    <button
                                                        onClick={() => respond(b.id, "confirmed")}
                                                        className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3.5 py-2 rounded-xl"
                                                    >
                                                        <CheckCircle2 size={13} /> Accept
                                                    </button>
                                                    <button
                                                        onClick={() => respond(b.id, "declined")}
                                                        className="inline-flex items-center gap-1.5 text-slate-500 hover:text-rose-600 text-xs font-medium px-3.5 py-2 rounded-xl border border-slate-200 hover:bg-rose-50"
                                                    >
                                                        <XCircle size={13} /> Decline
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3.5 py-2 rounded-xl">
                                                        <Video size={13} /> Join Call
                                                    </button>
                                                    <button className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-xs font-medium px-3.5 py-2 rounded-xl border border-slate-200 hover:bg-slate-50">
                                                        <MessageSquare size={13} /> Message
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {filtered.length === 0 && (
                                <div className="px-6 py-10 text-center text-slate-400 text-sm">
                                    No bookings match your search.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Reviews */}
                    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h2 className="font-bold text-slate-900 text-[15px]">Recent Reviews</h2>
                            <span className="flex items-center gap-1 text-xs font-semibold text-slate-700">
                                <Star size={12} className="fill-amber-400 text-amber-400" /> {expert.rating}
                            </span>
                        </div>
                        <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto custom-scrollbar">
                            {reviews.map((r, i) => (
                                <div key={i} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-slate-800 text-sm">{r.user}</span>
                                        <span className="flex items-center gap-0.5">
                                            {Array.from({ length: 5 }).map((_, s) => (
                                                <Star
                                                    key={s}
                                                    size={12}
                                                    className={
                                                        s < r.rating
                                                            ? "fill-amber-400 text-amber-400"
                                                            : "fill-slate-200 text-slate-200"
                                                    }
                                                />
                                            ))}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1.5">{r.comment}</p>
                                </div>
                            ))}
                        </div>
                        <div className="px-6 py-3 border-t border-slate-100">
                            <button className="w-full text-center text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1">
                                View all reviews <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}